"use client";

import { useState } from "react";
import {
  PrivyProvider,
  usePrivy,
  useLoginWithEmail,
  useWallets,
} from "@privy-io/react-auth";
import { useSendTransaction } from "@privy-io/react-auth";
import {
  createPublicClient,
  http,
  decodeErrorResult,
  encodeFunctionData,
  keccak256,
  concat,
  hexToBytes,
  recoverAddress,
} from "viem";
import { baseSepolia } from "viem/chains";
import Button from "./Button";
// import Input from "./Input";
import TextArea from "./TextArea";
import { AlertError } from "./AlertError";
import Success from "./Success";
import Spinner from "./Spinner";
import {
  CONTRACT_ABI,
  CONTRACT_ADDRESS,
  PARENT_CONTRACT_ABI,
} from "@/utils/contract";

const publicClient = createPublicClient({
  chain: baseSepolia,
  transport: http(),
});

// Simple Input component for this use case
const Input = ({ value, onChange, placeholder, className = "" }: any) => (
  <input
    type="text"
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    className={`bg-white dark:bg-black/20 block w-full rounded-md border py-1 px-2 placeholder:dark:text-white/10 placeholder:text-black/10 border-primary dark:border-primary-dark outline-none ${className}`}
  />
);

function VerifyProofsContent() {
  const { ready, authenticated, user, login, logout } = usePrivy();
  const { wallets } = useWallets();
  const { sendTransaction } = useSendTransaction();
  const { sendCode, loginWithCode } = useLoginWithEmail();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [dryRunResult, setDryRunResult] = useState<string | null>(null);
  const [debugResults, setDebugResults] = useState<string | null>(null);
  const [addNotaryAddress, setAddNotaryAddress] = useState("");

  // Email login state
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [showCodeInput, setShowCodeInput] = useState(false);

  // Proof verification inputs
  const [attestationJson, setAttestationJson] = useState("");
  const [useManualInput, setUseManualInput] = useState(false);
  const [manualInputs, setManualInputs] = useState({
    // AttestationInput fields
    version: "",
    scriptRaw: "",
    issuedAt: "",
    nonce: "",
    sessionId: "",
    proofKeys: "",
    proofValues: "",

    // AttestationSignature fields
    digest: "",
    v: "",
    r: "",
    s: "",
    signer: "",
  });

  const handleSendCode = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await sendCode({ email });
      setShowCodeInput(true);
    } catch (err) {
      setError("Failed to send verification code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await loginWithCode({ code });
    } catch (err) {
      setError("Invalid verification code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleWalletConnect = () => {
    login();
  };

  const parseAttestationJson = () => {
    try {
      const attestation = JSON.parse(attestationJson);

      // Extract script raw content
      const scriptRaw = attestation.script?.raw || "";

      // Extract proof data and convert to array format
      const proofData: { key: string; value: string }[] = [];
      if (attestation.data && typeof attestation.data === "object") {
        // Sort keys for deterministic ordering (as per contract requirement)
        const sortedKeys = Object.keys(attestation.data).sort();
        for (const key of sortedKeys) {
          proofData.push({
            key: key,
            value: String(attestation.data[key]),
          });
        }
      }

      return {
        // AttestationInput fields
        version: attestation.version || "",
        scriptRaw: scriptRaw,
        issuedAt: attestation.issued_at || "",
        nonce: attestation.nonce || "",
        sessionId: attestation.session_id || "",
        data: proofData,

        // AttestationSignature fields
        digest: attestation.digest || "",
        v: attestation.signature_v?.toString() || "",
        r: attestation.signature_r || "",
        s: attestation.signature_s || "",
        signer: attestation.signer || "",
      };
    } catch (err) {
      throw new Error("Invalid JSON format");
    }
  };

  const handleVerifyProof = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccess(false);
      setTxHash(null);

      const wallet = wallets[0];
      if (!wallet) {
        throw new Error("No wallet connected. Please connect a wallet first.");
      }

      // Make sure wallet is on the right chain
      if (wallet.chainId !== "eip155:84532") {
        throw new Error("Please switch to Base Sepolia network");
      }

      let params;
      if (useManualInput) {
        // Parse comma-separated keys and values for manual input
        const keys = manualInputs.proofKeys
          .split(",")
          .map((k) => k.trim())
          .filter((k) => k);
        const values = manualInputs.proofValues
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v);

        if (keys.length !== values.length) {
          throw new Error("Number of proof keys and values must match");
        }

        const proofData = keys.map((key, index) => ({
          key: key,
          value: values[index],
        }));

        params = {
          // AttestationInput fields
          version: manualInputs.version,
          scriptRaw: manualInputs.scriptRaw,
          issuedAt: manualInputs.issuedAt,
          nonce: manualInputs.nonce,
          sessionId: manualInputs.sessionId,
          data: proofData,

          // AttestationSignature fields
          digest: manualInputs.digest.startsWith("0x")
            ? manualInputs.digest
            : `0x${manualInputs.digest}`,
          v: manualInputs.v,
          r: manualInputs.r.startsWith("0x")
            ? manualInputs.r
            : `0x${manualInputs.r}`,
          s: manualInputs.s.startsWith("0x")
            ? manualInputs.s
            : `0x${manualInputs.s}`,
          signer: manualInputs.signer.startsWith("0x")
            ? manualInputs.signer
            : `0x${manualInputs.signer}`,
        };
      } else {
        params = parseAttestationJson();
      }

      // Validate inputs
      if (
        !params.version ||
        !params.scriptRaw ||
        !params.issuedAt ||
        !params.nonce ||
        !params.sessionId ||
        !params.digest ||
        !params.v ||
        !params.r ||
        !params.s ||
        !params.signer
      ) {
        throw new Error("Missing required fields");
      }

      // Prepare AttestationInput struct
      const attestationInput = {
        version: params.version,
        scriptRaw: params.scriptRaw,
        issuedAt: params.issuedAt,
        nonce: params.nonce,
        sessionId: params.sessionId,
        data: params.data || [],
      };

      // Prepare AttestationSignature struct
      const attestationSignature = {
        digest: params.digest,
        v: parseInt(params.v),
        r: params.r,
        s: params.s,
        expectedSigner: params.signer,
      };

      console.log("AttestationInput", attestationInput);
      console.log("AttestationSignature", attestationSignature);

      // Encode function data
      const data = encodeFunctionData({
        abi: CONTRACT_ABI,
        functionName: "verifyAttestation",
        args: [attestationInput, attestationSignature],
      });

      // Send transaction
      const tx = await sendTransaction(
        {
          from: wallet.address,
          to: CONTRACT_ADDRESS,
          data,
          chainId: 84532, // Base Sepolia
        },
        { address: wallet.address }
      );

      setTxHash(tx.hash);

      // Wait for transaction receipt to confirm success
      try {
        const receipt = await publicClient.waitForTransactionReceipt({
          hash: tx.hash as `0x${string}`,
          timeout: 60000, // 60 second timeout
        });

        if (receipt.status === "success") {
          setSuccess(true);
        } else {
          throw new Error("Transaction failed");
        }
      } catch (receiptError) {
        throw new Error(`Transaction failed: ${receiptError}`);
      }
    } catch (err: any) {
      console.log("Error:", err);
      setError(err.message || "Failed to verify proof");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDryRun = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccess(false);
      setTxHash(null);
      setDryRunResult(null);

      const wallet = wallets[0];
      if (!wallet) {
        throw new Error("No wallet connected. Please connect a wallet first.");
      }

      let params;
      if (useManualInput) {
        // Parse comma-separated keys and values for manual input
        const keys = manualInputs.proofKeys
          .split(",")
          .map((k) => k.trim())
          .filter((k) => k);
        const values = manualInputs.proofValues
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v);

        if (keys.length !== values.length) {
          throw new Error("Number of proof keys and values must match");
        }

        const proofData = keys.map((key, index) => ({
          key: key,
          value: values[index],
        }));

        params = {
          // AttestationInput fields
          version: manualInputs.version,
          scriptRaw: manualInputs.scriptRaw,
          issuedAt: manualInputs.issuedAt,
          nonce: manualInputs.nonce,
          sessionId: manualInputs.sessionId,
          data: proofData,

          // AttestationSignature fields
          digest: manualInputs.digest.startsWith("0x")
            ? manualInputs.digest
            : `0x${manualInputs.digest}`,
          v: manualInputs.v,
          r: manualInputs.r.startsWith("0x")
            ? manualInputs.r
            : `0x${manualInputs.r}`,
          s: manualInputs.s.startsWith("0x")
            ? manualInputs.s
            : `0x${manualInputs.s}`,
          signer: manualInputs.signer.startsWith("0x")
            ? manualInputs.signer
            : `0x${manualInputs.signer}`,
        };
      } else {
        params = parseAttestationJson();
      }

      // Validate inputs
      if (
        !params.version ||
        !params.scriptRaw ||
        !params.issuedAt ||
        !params.nonce ||
        !params.sessionId ||
        !params.digest ||
        !params.v ||
        !params.r ||
        !params.s ||
        !params.signer
      ) {
        throw new Error("Missing required fields");
      }

      // Prepare AttestationInput struct
      const attestationInput = {
        version: params.version,
        scriptRaw: params.scriptRaw,
        issuedAt: params.issuedAt,
        nonce: params.nonce,
        sessionId: params.sessionId,
        data: params.data || [],
      };

      // Prepare AttestationSignature struct
      const attestationSignature = {
        digest: params.digest,
        v: parseInt(params.v),
        r: params.r,
        s: params.s,
        expectedSigner: params.signer,
      };

      const data = encodeFunctionData({
        abi: CONTRACT_ABI,
        functionName: "verifyAttestation",
        args: [attestationInput, attestationSignature],
      });

      await publicClient.call({
        account: wallet.address as `0x${string}`,
        to: CONTRACT_ADDRESS,
        data,
      });

      setDryRunResult("Dry run successful: Transaction would succeed.");
    } catch (err: any) {
      console.log("Dry Run Error:", err);
      if (err.cause && err.cause.data) {
        const decodedError = decodeErrorResult({
          abi: CONTRACT_ABI,
          data: err.cause.data,
        });
        setDryRunResult(`Dry run failed: ${decodedError.errorName}`);
      } else {
        setDryRunResult(`Dry run failed: ${err.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const debugRevertReason = async (params: any) => {
    const wallet = wallets[0];
    if (!wallet) throw new Error("No wallet connected");

    let debugInfo = "🔍 Debugging revert reason:\n\n";

    try {
      // Prepare AttestationInput struct
      const attestationInput = {
        version: params.version,
        scriptRaw: params.scriptRaw,
        issuedAt: params.issuedAt,
        nonce: params.nonce,
        sessionId: params.sessionId,
        data: params.data || [],
      };

      // Prepare AttestationSignature struct
      const attestationSignature = {
        digest: params.digest,
        v: parseInt(params.v),
        r: params.r,
        s: params.s,
        expectedSigner: params.signer,
      };

      // 1. Check if call would succeed first
      const data = encodeFunctionData({
        abi: CONTRACT_ABI,
        functionName: "verifyAttestation",
        args: [attestationInput, attestationSignature],
      });

      const problem = await publicClient
        .call({
          account: wallet.address as `0x${string}`,
          to: CONTRACT_ADDRESS,
          data,
        })
        .then(() => "none")
        .catch((err) => {
          console.log("Call error:", err);
          if (err.cause && err.cause.data) {
            const decoded = decodeErrorResult({
              abi: CONTRACT_ABI,
              data: err.cause.data,
            });
            return decoded.errorName;
          }
          return "unknown";
        });

      debugInfo += `📋 Problem detected: ${problem}\n\n`;

      // 2. Check if signer is a valid notary via the verifier contract
      try {
        const verifierAddress = await publicClient.readContract({
          abi: CONTRACT_ABI,
          address: CONTRACT_ADDRESS,
          functionName: "verifier",
        });

        const [isNotaryResult] = await Promise.all([
          publicClient.readContract({
            abi: PARENT_CONTRACT_ABI,
            address: verifierAddress as `0x${string}`,
            functionName: "isNotary",
            args: [params.signer as `0x${string}`],
          }),
        ]);

        debugInfo += `👤 Notary check: ${
          isNotaryResult ? "✅ Valid notary" : "❌ Invalid notary"
        }\n`;
        debugInfo += `   Signer: ${params.signer}\n`;
        debugInfo += `   Verifier Contract: ${verifierAddress}\n`;
        debugInfo += `   Your Wallet: ${wallet.address}\n\n`;
      } catch (err) {
        debugInfo += `👤 Notary check: ❌ Failed to check - ${err}\n\n`;
      }

      // 3. Verify hash calculations using contract functions
      try {
        const [calculatedHashes] = await Promise.all([
          publicClient.readContract({
            abi: CONTRACT_ABI,
            address: CONTRACT_ADDRESS,
            functionName: "getCalculatedHashes",
            args: [attestationInput],
          }),
        ]);

        const [scriptHash, sessionHash, calculatedDigest] =
          calculatedHashes as [string, string, string];

        const digestMatch =
          calculatedDigest.toLowerCase() === params.digest.toLowerCase();
        debugInfo += `🔢 Hash verification:\n`;
        debugInfo += `   Expected digest: ${params.digest}\n`;
        debugInfo += `   Calculated digest: ${calculatedDigest}\n`;
        debugInfo += `   Match: ${digestMatch ? "✅" : "❌"}\n`;
        debugInfo += `   Script hash: ${scriptHash}\n`;
        debugInfo += `   Session hash: ${sessionHash}\n\n`;

        debugInfo += `📝 Input data verification:\n`;
        debugInfo += `   Version: ${params.version}\n`;
        debugInfo += `   Script length: ${params.scriptRaw.length} chars\n`;
        debugInfo += `   Issued at: ${params.issuedAt}\n`;
        debugInfo += `   Nonce: ${params.nonce}\n`;
        debugInfo += `   Session ID: ${params.sessionId}\n`;
        debugInfo += `   Proof data entries: ${params.data?.length || 0}\n`;
        if (params.data && params.data.length > 0) {
          params.data.forEach((item: any, index: number) => {
            debugInfo += `     ${index + 1}. ${item.key}: ${item.value}\n`;
          });
        }
        debugInfo += `\n`;
      } catch (err) {
        debugInfo += `🔢 Hash verification: ❌ Failed to calculate - ${err}\n\n`;
      }

      // 4. Verify signature recovery
      try {
        // Validate that r and s are proper 32-byte hex strings
        if (
          !params.r ||
          params.r.length !== 66 ||
          !params.s ||
          params.s.length !== 66
        ) {
          throw new Error(
            `Invalid r/s format: r=${params.r?.length}, s=${params.s?.length}`
          );
        }

        // Use the same ecrecover logic as the contract
        const signature = `${params.r}${params.s.slice(2)}${parseInt(params.v)
          .toString(16)
          .padStart(2, "0")}` as `0x${string}`;
        const recovered = await recoverAddress({
          hash: params.digest as `0x${string}`,
          signature,
        });

        const signatureMatch =
          recovered.toLowerCase() === params.signer.toLowerCase();
        debugInfo += `✍️ Signature verification:\n`;
        debugInfo += `   Expected signer: ${params.signer}\n`;
        debugInfo += `   Recovered signer: ${recovered}\n`;
        debugInfo += `   Match: ${signatureMatch ? "✅" : "❌"}\n`;
        debugInfo += `   V: ${params.v} (should be 27 or 28)\n`;
        debugInfo += `   R: ${params.r}\n`;
        debugInfo += `   S: ${params.s}\n\n`;
      } catch (err) {
        debugInfo += `✍️ Signature verification: ❌ Failed to recover - ${err}\n\n`;
      }

      // 5. Check for duplicate proofs via verifier contract
      try {
        const verifierAddress = await publicClient.readContract({
          abi: CONTRACT_ABI,
          address: CONTRACT_ADDRESS,
          functionName: "verifier",
        });

        const firstSubmitter = await publicClient.readContract({
          abi: PARENT_CONTRACT_ABI,
          address: verifierAddress as `0x${string}`,
          functionName: "digests",
          args: [params.digest as `0x${string}`],
        });

        const isDuplicate =
          firstSubmitter !== "0x0000000000000000000000000000000000000000";
        debugInfo += `🔄 Duplicate check:\n`;
        debugInfo += `   Already submitted: ${
          isDuplicate ? "❌ Yes" : "✅ No"
        }\n`;
        if (isDuplicate) {
          debugInfo += `   First submitter: ${firstSubmitter}\n`;
          debugInfo += `   Current wallet: ${wallet.address}\n`;
          debugInfo += `   Same wallet: ${
            (firstSubmitter as string).toLowerCase() ===
            wallet.address.toLowerCase()
              ? "Yes"
              : "No"
          }\n`;
        }
        debugInfo += `\n`;
      } catch (err) {
        debugInfo += `🔄 Duplicate check: ❌ Failed to check - ${err}\n\n`;
      }

      // 6. Summary based on problem
      switch (problem) {
        case "InvalidNotary":
          debugInfo += `🎯 SOLUTION: The signer address (${params.signer}) is not registered as a notary.\n`;
          debugInfo += `   • Use the correct notary address from your attestation\n`;
          debugInfo += `   • Ask the verifier contract owner to call addNotary() for this address\n`;
          break;
        case "InvalidDigest":
          debugInfo += `🎯 SOLUTION: The digest doesn't match the calculated hash.\n`;
          debugInfo += `   • Check if script content, version, timestamps, nonce, sessionId, and proof data are correct\n`;
          debugInfo += `   • Ensure proof data keys are sorted alphabetically\n`;
          debugInfo += `   • Verify you're using the exact same input data that was used to generate the attestation\n`;
          break;
        case "InvalidSignature":
          debugInfo += `🎯 SOLUTION: The signature (r,s,v) doesn't recover to the expected signer.\n`;
          debugInfo += `   • Verify v is exactly 27 or 28 (don't subtract 27)\n`;
          debugInfo += `   • Ensure r and s are 32-byte hex strings (0x + 64 chars)\n`;
          debugInfo += `   • Check if any leading zeros were stripped\n`;
          break;
        case "DuplicateProof":
          debugInfo += `🎯 SOLUTION: This proof has already been submitted.\n`;
          debugInfo += `   • Generate a new attestation with different nonce\n`;
          debugInfo += `   • Each proof can only be submitted once per wallet (anti-replay protection)\n`;
          break;
        case "none":
          debugInfo += `🎯 SUCCESS: The transaction would succeed! ✅\n`;
          break;
        default:
          debugInfo += `🎯 UNKNOWN ERROR: ${problem}\n`;
          debugInfo += `   • Check contract ABI and function parameters\n`;
          debugInfo += `   • Verify contract address and network\n`;
          debugInfo += `   • Ensure the AttestationInput struct matches exactly\n`;
      }

      return debugInfo;
    } catch (err: any) {
      debugInfo += `❌ Debug failed: ${err.message}\n`;
      return debugInfo;
    }
  };

  const handleDebugRevert = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setDebugResults(null);

      const wallet = wallets[0];
      if (!wallet) {
        throw new Error("No wallet connected. Please connect a wallet first.");
      }

      let params;
      if (useManualInput) {
        // Parse comma-separated keys and values for manual input
        const keys = manualInputs.proofKeys
          .split(",")
          .map((k) => k.trim())
          .filter((k) => k);
        const values = manualInputs.proofValues
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v);

        if (keys.length !== values.length) {
          throw new Error("Number of proof keys and values must match");
        }

        const proofData = keys.map((key, index) => ({
          key: key,
          value: values[index],
        }));

        params = {
          // AttestationInput fields
          version: manualInputs.version,
          scriptRaw: manualInputs.scriptRaw,
          issuedAt: manualInputs.issuedAt,
          nonce: manualInputs.nonce,
          sessionId: manualInputs.sessionId,
          data: proofData,

          // AttestationSignature fields
          digest: manualInputs.digest.startsWith("0x")
            ? manualInputs.digest
            : `0x${manualInputs.digest}`,
          v: manualInputs.v,
          r: manualInputs.r.startsWith("0x")
            ? manualInputs.r
            : `0x${manualInputs.r}`,
          s: manualInputs.s.startsWith("0x")
            ? manualInputs.s
            : `0x${manualInputs.s}`,
          signer: manualInputs.signer.startsWith("0x")
            ? manualInputs.signer
            : `0x${manualInputs.signer}`,
        };
      } else {
        params = parseAttestationJson();
      }

      // Validate inputs
      if (
        !params.version ||
        !params.scriptRaw ||
        !params.issuedAt ||
        !params.nonce ||
        !params.sessionId ||
        !params.digest ||
        !params.v ||
        !params.r ||
        !params.s ||
        !params.signer
      ) {
        throw new Error("Missing required fields");
      }

      const debugInfo = await debugRevertReason(params);
      setDebugResults(debugInfo);
    } catch (err: any) {
      console.log("Debug Error:", err);
      setError(err.message || "Failed to debug");
    } finally {
      setIsLoading(false);
    }
  };

  if (!ready) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mt-8  dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900">
      <h2 className=" mb-4">
        Connect your wallet and input your attestation to verify onchain.
      </h2>

      {!authenticated ? (
        <div className="space-y-4">
          <div className="mb-6">
            <div className="space-y-4">
              <Button onClick={handleWalletConnect}>Connect Wallet</Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            {/* <div className="text-sm text-gray-600 dark:text-gray-400">
              Connected as:{" "}
              {user?.email?.address ||
                wallets[0]?.address?.slice(0, 6) +
                  "..." +
                  wallets[0]?.address?.slice(-4)}
            </div> */}
            <Button onClick={logout} className="text-sm">
              Disconnect
            </Button>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-4 mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={!useManualInput}
                  onChange={() => setUseManualInput(false)}
                />
                <span>Paste Attestation JSON</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={useManualInput}
                  onChange={() => setUseManualInput(true)}
                />
                <span>Manual Input</span>
              </label>
            </div>

            {!useManualInput ? (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Attestation JSON
                </label>
                <TextArea
                  placeholder="Paste your attestation JSON here..."
                  value={attestationJson}
                  onChange={(e) => setAttestationJson(e.target.value)}
                  rows={10}
                  className="font-mono text-sm"
                />
              </div>
            ) : (
              <div className="space-y-3">
                <h3 className="font-semibold text-lg">Attestation Input</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Version
                    </label>
                    <Input
                      placeholder="v1"
                      value={manualInputs.version}
                      onChange={(e) =>
                        setManualInputs({
                          ...manualInputs,
                          version: e.target.value,
                        })
                      }
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Issued At
                    </label>
                    <Input
                      placeholder="2025-07-03T15:03:57Z"
                      value={manualInputs.issuedAt}
                      onChange={(e) =>
                        setManualInputs({
                          ...manualInputs,
                          issuedAt: e.target.value,
                        })
                      }
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Nonce
                    </label>
                    <Input
                      placeholder="0x3935659ffb235e15"
                      value={manualInputs.nonce}
                      onChange={(e) =>
                        setManualInputs({
                          ...manualInputs,
                          nonce: e.target.value,
                        })
                      }
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Session ID
                    </label>
                    <Input
                      placeholder="8bc45a6a-7b61-4828-a513-f6913535547a"
                      value={manualInputs.sessionId}
                      onChange={(e) =>
                        setManualInputs({
                          ...manualInputs,
                          sessionId: e.target.value,
                        })
                      }
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Script Raw
                  </label>
                  <TextArea
                    placeholder="import { createSession } from '@plutoxyz/automation'..."
                    value={manualInputs.scriptRaw}
                    onChange={(e) =>
                      setManualInputs({
                        ...manualInputs,
                        scriptRaw: e.target.value,
                      })
                    }
                    rows={4}
                    className="font-mono text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Proof Keys (comma-separated)
                    </label>
                    <Input
                      placeholder="bank_balance,user_id"
                      value={manualInputs.proofKeys}
                      onChange={(e) =>
                        setManualInputs({
                          ...manualInputs,
                          proofKeys: e.target.value,
                        })
                      }
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Proof Values (comma-separated)
                    </label>
                    <Input
                      placeholder="8910.3,12345"
                      value={manualInputs.proofValues}
                      onChange={(e) =>
                        setManualInputs({
                          ...manualInputs,
                          proofValues: e.target.value,
                        })
                      }
                      className="font-mono text-sm"
                    />
                  </div>
                </div>

                <h3 className="font-semibold text-lg mt-6">
                  Attestation Signature
                </h3>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Digest
                  </label>
                  <Input
                    placeholder="0x..."
                    value={manualInputs.digest}
                    onChange={(e) =>
                      setManualInputs({
                        ...manualInputs,
                        digest: e.target.value,
                      })
                    }
                    className="font-mono text-sm"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">V</label>
                    <Input
                      placeholder="27 or 28"
                      value={manualInputs.v}
                      onChange={(e) =>
                        setManualInputs({ ...manualInputs, v: e.target.value })
                      }
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">R</label>
                    <Input
                      placeholder="0x..."
                      value={manualInputs.r}
                      onChange={(e) =>
                        setManualInputs({
                          ...manualInputs,
                          r: e.target.value,
                        })
                      }
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">S</label>
                    <Input
                      placeholder="0x..."
                      value={manualInputs.s}
                      onChange={(e) =>
                        setManualInputs({
                          ...manualInputs,
                          s: e.target.value,
                        })
                      }
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Expected Signer Address
                  </label>
                  <Input
                    placeholder="0x..."
                    value={manualInputs.signer}
                    onChange={(e) =>
                      setManualInputs({
                        ...manualInputs,
                        signer: e.target.value,
                      })
                    }
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-4 flex-wrap">
              <Button
                onClick={handleVerifyProof}
                disabled={isLoading || (!useManualInput && !attestationJson)}
              >
                {isLoading ? "Verifying..." : "Verify Proof on Base Sepolia"}
              </Button>
              {/* <Button
                onClick={handleDryRun}
                disabled={isLoading || (!useManualInput && !attestationJson)}
              >
                Dry Run
              </Button> */}
              <Button
                onClick={handleDebugRevert}
                disabled={isLoading || (!useManualInput && !attestationJson)}
                className=""
              >
                🔍 Debug
              </Button>
            </div>

            {error && <AlertError errorMessage={error} />}

            {dryRunResult && (
              <div className="mt-4 p-3 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm">
                {dryRunResult}
              </div>
            )}

            {debugResults && (
              <div className="mt-4 p-4 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
                  Debug Results:
                </h3>
                <pre className="text-xs text-blue-700 dark:text-blue-300 whitespace-pre-wrap font-mono overflow-x-auto">
                  {debugResults}
                </pre>
              </div>
            )}

            {success && txHash && (
              <div className="flex flex-col space-y-2">
                <Success text="Proof verification confirmed successfully!" />
                <a
                  href={`https://sepolia.basescan.org/tx/${txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-600 text-sm"
                >
                  View transaction →
                </a>
              </div>
            )}

            {txHash && !success && !error && (
              <div className="flex flex-col space-y-2">
                <div className="text-yellow-600 dark:text-yellow-400 text-sm">
                  ⏳ Transaction submitted, waiting for confirmation...
                </div>
                <a
                  href={`https://sepolia.basescan.org/tx/${txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-600 text-sm"
                >
                  View transaction →
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function VerifyProofs() {
  const appId = process.env.NEXT_PUBLIC_PRIVY_APP_ID || "";

  return (
    <PrivyProvider
      appId={appId}
      config={{
        // Appearance
        appearance: {
          theme: "light",
          accentColor: "#676FFF",
        },
        // Login methods
        loginMethods: ["email", "wallet"],
        // Embedded wallets
        embeddedWallets: {
          createOnLogin: "users-without-wallets",
          requireUserPasswordOnCreate: false,
        },
        // Networks
        defaultChain: baseSepolia,
        supportedChains: [baseSepolia],
        // External wallets
      }}
    >
      <VerifyProofsContent />
    </PrivyProvider>
  );
}
